// lib/keyboard-shortcuts.ts

export const STUDIO_TOOL_SHORTCUTS = [
  {
    id: 'select',
    tool: null,
    defaultChord: 'V',
    command: '/SELECT_TOOL name:None silent:true',
    label: 'Select tool',
  },
  {
    id: 'generationArea',
    tool: 'canvasControl',
    defaultChord: 'F',
    command: '/SELECT_TOOL name:CanvasControl silent:true',
    label: 'Generation area',
  },
  {
    id: 'cutOrSplit',
    tool: 'scissors',
    defaultChord: 'S',
    command: '/SELECT_TOOL name:Scissors silent:true',
    label: 'Cut or split',
  },
  {
    id: 'annotationPen',
    tool: 'annotationLine',
    defaultChord: 'B',
    command: '/SELECT_TOOL name:AnnotationLine silent:true',
    label: 'Annotation pen',
  },
  {
    id: 'comment',
    tool: 'comment',
    defaultChord: 'C',
    command: '/SELECT_TOOL name:AnnotationComment silent:true',
    label: 'Comment tool',
  },
  {
    id: 'annotationEraser',
    tool: 'annotationEraser',
    defaultChord: 'E',
    command: '/SELECT_TOOL name:AnnotationEraser silent:true',
    label: 'Annotation eraser',
  },
  {
    id: 'draw',
    tool: 'draw',
    defaultChord: 'U',
    command: '/SELECT_TOOL name:Draw silent:true',
    label: 'Shape tool',
  },
  {
    id: 'mask',
    tool: 'maskPen',
    defaultChord: 'M',
    command: '/SELECT_TOOL name:MaskPen silent:true',
    label: 'Mask tool',
  },
] as const;

export const DEFAULT_KEYBOARD_SHORTCUTS: Record<string, string> = {
  'Control+H': '/SHOW_MODAL name:ActionHistory',
  'Control+Z': '/UNDO',
  'Control+Shift+Z': '/REDO',
  'Control+Y': '/REDO',
  ...Object.fromEntries(
    STUDIO_TOOL_SHORTCUTS.map(({ defaultChord, command }) => [defaultChord, command]),
  ),
};

const normalizeCommand = (command: string): string => command.trim().replace(/\s+/g, ' ');

// Commands of tools that no longer exist, still sitting in saved preferences: running one throws.
const RETIRED_SHORTCUT_COMMANDS = new Set([
  '/SELECT_TOOL name:CameraControl3d silent:true',
  '/SELECT_TOOL name:CameraControl3d',
]);

export const isEditableKeyboardTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  return tagName === 'INPUT'
    || tagName === 'TEXTAREA'
    || tagName === 'SELECT'
    || target.isContentEditable
    || target.getAttribute('role') === 'textbox'
    || Boolean(target.closest('[contenteditable="true"], [role="textbox"]'));
};

/**
 * Studio keeps a surface mounted under the one shown (a project's canvas under its 3D scene, and the reverse), in an
 * inert box: its window-level listeners still hear every key, and must leave them to the surface on screen.
 * ponytail: asked by each of those listeners, one by one; keys dispatched once, to the surface on screen, would delete
 * every call.
 */
export const isInertSurface = (element: Element | null | undefined): boolean => Boolean(element?.closest('[inert]'));

export const shouldIgnoreKeyboardShortcutEvent = (
  event: KeyboardEvent,
  activeModal: unknown,
): boolean => (
  event.defaultPrevented
  || event.isComposing
  || event.keyCode === 229
  || event.repeat
  || Boolean(activeModal)
  || isEditableKeyboardTarget(event.target)
  || isEditableKeyboardTarget(document.activeElement)
);

export const findStudioToolShortcutByCommand = (command: string) => {
  const normalizedCommand = normalizeCommand(command);
  return STUDIO_TOOL_SHORTCUTS.find((shortcut) => shortcut.command === normalizedCommand);
};

export const findShortcutChordForCommand = (
  shortcuts: Record<string, string> | null | undefined,
  command: string,
): string | null => {
  const normalizedCommand = normalizeCommand(command);
  const match = Object.entries(shortcuts ?? {}).find(([, candidateCommand]) => (
    normalizeCommand(candidateCommand) === normalizedCommand
  ));
  return match?.[0] ?? null;
};

export const formatShortcutChordForDisplay = (shortcut: string): string => (
  shortcut.replace(/\bControl\b/g, 'Ctrl').replace(/\+/g, ' + ')
);

const MODIFIER_ORDER = ['Control', 'Shift', 'Alt'] as const;
const NAMED_KEYS: Record<string, string> = {
  arrowdown: 'ArrowDown',
  arrowleft: 'ArrowLeft',
  arrowright: 'ArrowRight',
  arrowup: 'ArrowUp',
  backspace: 'Backspace',
  delete: 'Delete',
  end: 'End',
  enter: 'Enter',
  escape: 'Escape',
  home: 'Home',
  pagedown: 'PageDown',
  pageup: 'PageUp',
  space: 'Space',
  tab: 'Tab',
};

export const normalizeShortcutChord = (shortcut: string): string | null => {
  const parts = shortcut.split('+').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return null;

  const modifiers = new Set<string>();
  let key: string | null = null;

  for (const part of parts) {
    const normalizedPart = part.toLowerCase();
    if (['control', 'ctrl', 'meta', 'cmd', 'command'].includes(normalizedPart)) {
      modifiers.add('Control');
      continue;
    }
    if (normalizedPart === 'shift') {
      modifiers.add('Shift');
      continue;
    }
    if (normalizedPart === 'alt' || normalizedPart === 'option') {
      modifiers.add('Alt');
      continue;
    }
    if (key) return null;
    key = part.length === 1
      ? part.toUpperCase()
      : NAMED_KEYS[normalizedPart] ?? `${part.charAt(0).toUpperCase()}${part.slice(1)}`;
  }

  if (!key) return null;
  return [
    ...MODIFIER_ORDER.filter((modifier) => modifiers.has(modifier)),
    key,
  ].join('+');
};

export const keyboardEventToShortcutChord = (
  event: KeyboardEvent,
  isMacPlatform: boolean,
): string => {
  const parts: string[] = [];
  const semanticControlKey = isMacPlatform ? event.metaKey : event.ctrlKey;
  if (semanticControlKey) parts.push('Control');
  if (event.shiftKey) parts.push('Shift');
  if (event.altKey) parts.push('Alt');

  let keyName = event.key === ' ' ? 'Space' : event.key;
  if (/^[a-z]$/i.test(keyName)) keyName = keyName.toUpperCase();
  parts.push(keyName);
  return parts.join('+');
};

/**
 * Adds newly shipped defaults without restoring the old chord of an action a user remapped.
 * A saved chord wins, and a command already bound elsewhere is treated as an intentional remap.
 */
export const mergeKeyboardShortcutDefaults = (
  savedShortcuts: Record<string, string> | null | undefined,
): Record<string, string> => {
  const savedEntries = Object.entries(savedShortcuts ?? {})
    .filter(([, command]) => !RETIRED_SHORTCUT_COMMANDS.has(normalizeCommand(command)));
  const merged: Record<string, string> = {};

  // Canonical saved chords win over legacy aliases such as Meta+Z or Shift+Ctrl+Z.
  for (const [savedChord, command] of savedEntries) {
    const canonicalChord = normalizeShortcutChord(savedChord);
    if (!canonicalChord || canonicalChord === savedChord) merged[savedChord] = command;
  }
  for (const [savedChord, command] of savedEntries) {
    const canonicalChord = normalizeShortcutChord(savedChord);
    if (canonicalChord && !Object.prototype.hasOwnProperty.call(merged, canonicalChord)) {
      merged[canonicalChord] = command;
    }
  }

  const savedCommands = new Set(Object.values(merged).map(normalizeCommand));

  for (const [defaultChord, defaultCommand] of Object.entries(DEFAULT_KEYBOARD_SHORTCUTS)) {
    if (Object.prototype.hasOwnProperty.call(merged, defaultChord)) continue;
    if (savedCommands.has(normalizeCommand(defaultCommand))) continue;
    merged[defaultChord] = defaultCommand;
    savedCommands.add(normalizeCommand(defaultCommand));
  }

  return merged;
};
